import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { components, type SingleValueProps } from 'react-select';
import { produce } from 'immer';
import { type ConditionProperties } from 'json-rules-engine';
import _ from 'lodash';
import { SButton, SIcon, SLabel } from '@chargebee/sting-react';
import { ConditionValueInputRenderer } from '@bbkAdminComponents/experiences/audiences/edit-rule/ConditionValueInputRenderer';
import { EditRuleContext } from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-context';
import {
  type AudienceFact,
  type CustomOptionData,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-types';
import { getOperatorsForType } from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import {
  AIIcons,
  RetentionAIText,
} from '@bbkAdminComponents/experiences/offers/ai-icons';
import { Label } from '@bbkAdminComponents/inputs/label/label';
import {
  GenericSelect,
  type SelectOption,
} from '@bbkAdminComponents/select/select';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  AppFieldType,
  IntegrationDataType,
} from '@bbkAdminRedux/data-management/reducers';
import {
  AI_RESERVED_CHURN_RISK_LEVEL,
  AI_RESERVED_CHURN_RISK_SCORE,
  AI_RULE_RESERVED_KEY_PREFIX,
  createFieldsWithPrefixes,
  integrationSlice,
  OFFER_RESERVED_FIELDS,
  RETENTION_OFFER_RESERVED_FIELD_NAME,
  selectFields,
  useSelectBillingIntegration,
  useSelectReservedFields,
} from '@bbkAdminRedux/rtkq/integrations.slice';
import { type PatchPath } from '@bbkAdminUtils/utility-types';

// Re-export for backward compatibility
export {
  type AudienceFact,
  type CustomOptionData,
  NullishOperator,
  nullishOperators,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-types';

const LOV_INFO_MESSAGE =
  'Select from list of available values or type to search for more. ' +
  'By default the first 100 values from your live billing site are displayed. ' +
  'You may choose to add custom values for testing purposes as well.';

const allowTooltipFor = new Set([
  'standard.Coupon Codes',
  'standard.Coupon IDs',
  'standard.Plan ID',
  'standard.Plan Interval',
  'custom.Subscription Status',
]);

type ConditionItemProps = {
  condition: ConditionProperties;
  path: PatchPath;
};

type PropertySelectOption = SelectOption<string> & { field: AudienceFact };

export const CustomOptionRenderer: React.FC<
  React.ComponentProps<typeof components.Option>
> = (props) => {
  const data = props.data as CustomOptionData;

  return (
    <components.Option {...props}>
      <div className="tw-flex tw-items-center">
        <span>{data.label}</span>
        {data.icon && <span className="tw-ml-1">{data.icon}</span>}
      </div>
    </components.Option>
  );
};
const predictiveFields = [
  AI_RESERVED_CHURN_RISK_LEVEL,
  AI_RESERVED_CHURN_RISK_SCORE,
];
const retentionTagIcon = (label: string) => {
  return predictiveFields.includes(label) ? AIIcons.predictiveAiTag : undefined;
};
export const CustomValueRenderer: React.FC<SingleValueProps> = ({
  children,
  ...props
}) => {
  const data = props.data as CustomOptionData;
  // For Audience Builder
  const icon = retentionTagIcon(data.label);

  // For Play Builder
  const newIconPresent = data.is_ai;
  const newIcon = data.icon;

  return (
    <components.SingleValue {...props}>
      <div className="tw-flex tw-items-center">
        <span>{children}</span>
        {icon && <span className="icon-container tw-ml-1">{icon}</span>}
        {newIconPresent && (
          <span className="icon-container tw-ml-1">{newIcon}</span>
        )}
      </div>
    </components.SingleValue>
  );
};

export const ConditionItem: React.FC<ConditionItemProps> = ({
  condition,
  path,
}) => {
  const appKey = useSelector(selectAppId);
  const { rule, setRule, properties } = useContext(EditRuleContext);
  const reservedFields = useSelectReservedFields();
  const offerIdReservedField = OFFER_RESERVED_FIELDS.find(
    (x) => x.name === RETENTION_OFFER_RESERVED_FIELD_NAME
  );
  const billingIntegration = useSelectBillingIntegration({ appKey });
  const isChargebeeIntegration =
    billingIntegration?.data_type === IntegrationDataType.CHARGEBEE;

  const { data: fieldsWithPrefixes } =
    integrationSlice.endpoints.getFieldMappings.useQuery(
      { appKey },
      {
        selectFromResult: (res) => ({
          ...res,
          data: createFieldsWithPrefixes([
            ...selectFields(res).data,
            ...reservedFields,
          ]),
        }),
      }
    );

  const allProperties = properties ?? fieldsWithPrefixes;

  const fieldWithPrefix = allProperties.find((f) => f.val === condition.fact);

  const propertiesOptions = allProperties.map((f) => ({
    label: f.name.replace(/^(custom|standard|reserved|alert|ai)\./, ''),
    value: f.val,
    field: f,
  }));

  const updatedPropertiesOptions = useMemo(() => {
    return [
      {
        label: (
          <RetentionAIText className="tw-ml-2 tw-text-sm tw-normal-case tw-text-black" />
        ),
        options: propertiesOptions
          .filter((option) => predictiveFields.includes(option.label))
          .map((option) => ({
            ...option,
            icon: retentionTagIcon(option.label),
          })),
      },
      ...propertiesOptions
        .filter((option) => !predictiveFields.includes(option.label))
        .map((option) => ({
          ...option,
          icon: undefined,
        })),
    ];
  }, [propertiesOptions]);

  const selectedProperty = propertiesOptions.find(
    (po) => po.value === condition.fact
  ) ?? {
    label: condition.fact,
    value: condition.fact,
  };

  const fieldType = fieldWithPrefix?.type;
  const operatorsOptions = useMemo(() => {
    const list = getOperatorsForType(fieldType);
    if (condition.fact === `reserved.${offerIdReservedField?.val}`) {
      return list.filter(
        (x) => x.value === 'String.in' || x.value === 'String.notIn'
      );
    }

    if (
      condition.fact ===
      `${AI_RULE_RESERVED_KEY_PREFIX}${AI_RESERVED_CHURN_RISK_LEVEL}`
    ) {
      return list.filter(
        (x) =>
          x.value === '==' ||
          x.value === '!=' ||
          x.value === 'String.in' ||
          x.value === 'String.notIn' ||
          x.value === 'Generic.isNilOrEmpty' ||
          x.value === 'Generic.isNotNilOrEmpty'
      );
    }

    return list;
  }, [
    condition,
    fieldType,
    offerIdReservedField,
    AI_RESERVED_CHURN_RISK_LEVEL,
  ]);
  const selectedOperator = operatorsOptions.find(
    (oo) => oo.value === condition.operator
  ) ?? {
    label: _.startCase(condition.operator),
    value: condition.operator,
  };

  const updateCondition = (
    updates: Partial<Pick<ConditionProperties, 'fact' | 'operator' | 'value'>>
  ) => {
    const updatedRule = produce(rule, (draft) => {
      Object.entries(updates).forEach(([key, value]) =>
        _.set(draft, [...path, key], value)
      );
    });
    setRule(updatedRule);
  };

  const deleteCondition = () => {
    const updatedRule = produce(rule, (draftState) => {
      const idx = path.slice(-1)[0];
      const parentPath = path.slice(0, -1);
      const group = _.get(draftState, parentPath);

      group.splice(idx, 1);

      // Removing whole group if it's last condition
      // unless it's top level parent
      if (group.length === 0 && parentPath.length > 1) {
        const groupIdx = parentPath.slice(-2)[0];
        const groupParentPath = parentPath.slice(0, -2);
        const groupParent = _.get(draftState, groupParentPath);

        groupParent?.splice(groupIdx, 1);
      }
    });

    setRule(updatedRule);
  };

  const addCondition = () => {
    const updatedRule = produce(rule, (draftState) => {
      const idx = path.slice(-1)[0] as number;
      const parentPath = path.slice(0, -1);
      const group = _.get(draftState, parentPath);

      const newCondition: ConditionProperties = {
        fact: '',
        operator: '',
        value: '',
      };
      group.splice(idx + 1, 0, newCondition);
    });

    setRule(updatedRule);
  };

  const isLovType =
    fieldType === AppFieldType.text &&
    (selectedOperator.value === '==' ||
      selectedOperator.value === '!=' ||
      selectedOperator.value === 'String.in' ||
      selectedOperator.value === 'String.notIn');
  const shouldShowTooltip =
    isLovType &&
    isChargebeeIntegration &&
    allowTooltipFor.has(selectedProperty.value);

  const input = (
    <ConditionValueInputRenderer
      fieldWithPrefix={fieldWithPrefix}
      operator={selectedOperator.value}
      condition={condition}
      onChange={updateCondition}
    />
  );

  const generateNewCondition = (e: PropertySelectOption) => {
    const type = e.field.type;
    const typeOperators = getOperatorsForType(type);

    const getDefaultValue = (
      type: AppFieldType | AppFieldType[keyof AppFieldType]
    ) => {
      switch (type) {
        case AppFieldType.boolean:
          return false;
        default:
          return '';
      }
    };

    return {
      fact: e.value,
      operator: typeOperators[0]?.value,
      value: getDefaultValue(type),
    };
  };

  return (
    <div className="tw-flex tw-gap-sm">
      <fieldset className="tw-min-w-0 tw-basis-[28%]">
        <SLabel>Property</SLabel>
        <GenericSelect
          options={updatedPropertiesOptions}
          placeholder="Select a property..."
          value={selectedProperty}
          onChange={(e) => {
            updateCondition(generateNewCondition(e));
          }}
          components={{
            Option: CustomOptionRenderer,
            SingleValue: CustomValueRenderer,
          }}
        />
      </fieldset>
      <fieldset className="tw-min-w-0 tw-basis-[28%]">
        <SLabel>Operator</SLabel>
        <GenericSelect
          options={operatorsOptions}
          placeholder="Select operator..."
          value={selectedOperator}
          onChange={(e) => updateCondition({ operator: e.value })}
        />
      </fieldset>
      {input && (
        <fieldset className="tw-min-w-0 tw-flex-1">
          <SLabel>
            Value
            {shouldShowTooltip && (
              <TippyTooltip
                content={
                  <span className="tw-text-sm tw-font-normal">
                    {LOV_INFO_MESSAGE}
                  </span>
                }
              >
                <SIcon className="tw-ml-1 tw-inline-block" name="info" />
              </TippyTooltip>
            )}
          </SLabel>
          {input}
          {condition.fact ===
            `${AI_RULE_RESERVED_KEY_PREFIX}${AI_RESERVED_CHURN_RISK_SCORE}` && (
            <Label>Low (1-39), Medium (40-69), High (70-100)</Label>
          )}
        </fieldset>
      )}
      <div className="tw-flex tw-flex-col tw-justify-end tw-pb-1">
        <div>
          <SButton
            icon={<SIcon name="trash-2" />}
            variant="danger-ghost"
            onClick={deleteCondition}
          />
          <SButton
            icon={<SIcon name="plus" />}
            variant="primary-ghost"
            onClick={addCondition}
          />
        </div>
      </div>
    </div>
  );
};
